import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { useClientConfig } from '../lib/useClientConfig';
import { SUPPORT_EMAIL } from '../lib/support';
import { TurnstileWidget } from './TurnstileWidget';

// The beta waitlist form (TOM-634, ADR 0384), shared by /beta and the home
// page's "Bring Neo aboard." section (TOM-980). A visitor leaves an email and
// POST /v1/beta-signup stores it. The answer is the same for a new address and
// one already on the list. Both pages keep the test ids beta-form, beta-joined
// and beta-error.
type Status = 'idle' | 'sending' | 'joined' | 'error';

const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

/** Class names for /beta's form ('page') and the home page's ('home'). */
const CLASSES = {
  page: {
    form: undefined,
    success: 'form-success',
    errors: 'form-errors',
    group: 'form-group',
    input: 'form-input',
    submit: 'btn btn-primary auth-submit',
  },
  home: {
    form: 'home-beta__form',
    success: 'home-beta__joined',
    errors: 'home-beta__errors',
    group: 'home-beta__group',
    input: 'home-beta__input',
    submit: 'home-btn home-btn--accent home-beta__submit',
  },
} as const;

export interface BetaSignupFormProps {
  variant: 'page' | 'home';
  /** The email input's id. */
  inputId: string;
  /** The submit button's text. */
  submitLabel: string;
}

export function BetaSignupForm({ variant, inputId, submitLabel }: BetaSignupFormProps) {
  const cls = CLASSES[variant];
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  // Turnstile (threat model E6): the same runtime site-key plumbing as signup
  // and contact; null in local dev and tests means no widget.
  const {
    config: clientConfig,
    loaded: configLoaded,
    error: configError,
    reload: reloadConfig,
  } = useClientConfig();
  const turnstileSiteKey = clientConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileWidgetKey, setTurnstileWidgetKey] = useState(0);
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const canSubmit =
    status !== 'sending' &&
    configLoaded &&
    !configError &&
    (turnstileSiteKey === null || turnstileToken !== null);

  function retryTurnstile() {
    setTurnstileFailed(false);
    setTurnstileWidgetKey((k) => k + 1);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('sending');
    setErrorMessage(null);
    try {
      const body: Record<string, string> = { email };
      if (turnstileToken !== null) body['cf-turnstile-response'] = turnstileToken;
      await api('/v1/beta-signup', { method: 'POST', body });
      setStatus('joined');
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      // The token was spent on this attempt; fetch a fresh one for the retry.
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setStatus('error');
      setErrorMessage(
        e.status === 429
          ? `Too many attempts. Please wait a while, or email ${SUPPORT_EMAIL}.`
          : (e.body?.message ?? `Something went wrong. Please email ${SUPPORT_EMAIL}.`)
      );
    }
  }

  if (status === 'joined') {
    return (
      <div className={cls.success} role="status" data-testid="beta-joined">
        <h3>You&apos;re on the list.</h3>
        <p>We&apos;ll email {email.trim()} when the beta has room for you.</p>
      </div>
    );
  }

  return (
    <form className={cls.form} onSubmit={onSubmit} data-testid="beta-form">
      {status === 'error' && errorMessage !== null ? (
        <ul className={cls.errors} role="alert" data-testid="beta-error">
          <li>{errorMessage}</li>
        </ul>
      ) : null}
      {configError ? (
        <ul className={cls.errors} role="alert">
          <li>
            We couldn&apos;t load verification.{' '}
            <button type="button" style={linkButtonStyle} onClick={reloadConfig}>
              Retry
            </button>
            , or email {SUPPORT_EMAIL}.
          </li>
        </ul>
      ) : null}
      <div className={cls.group}>
        <label htmlFor={inputId}>Email</label>
        <input
          type="email"
          name="email"
          id={inputId}
          className={cls.input}
          placeholder="you@example.com"
          autoComplete="email"
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      {turnstileSiteKey !== null && !turnstileFailed ? (
        <div className={cls.group}>
          <TurnstileWidget
            key={turnstileWidgetKey}
            siteKey={turnstileSiteKey}
            onToken={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileFailed(true)}
            resetSignal={turnstileNonce}
            theme={variant === 'home' ? 'dark' : undefined}
          />
        </div>
      ) : null}
      {turnstileFailed ? (
        <ul className={cls.errors} role="alert">
          <li>
            We couldn&apos;t finish a quick security check, usually a weak connection.{' '}
            <button type="button" style={linkButtonStyle} onClick={retryTurnstile}>
              Retry
            </button>
            , or email {SUPPORT_EMAIL}.
          </li>
        </ul>
      ) : null}
      <button type="submit" className={cls.submit} disabled={!canSubmit}>
        {status === 'sending' ? 'Joining…' : submitLabel}
      </button>
      {variant === 'page' ? (
        <p className="auth-foot">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      ) : null}
    </form>
  );
}
